import { useState } from 'react'

import {
  Box,
  Colors,
  CounterBadge,
  Icon,
  InputCheckBox,
  SearchField,
  Text,
} from '@factorialco/deprecated-design-system'
import { xor } from 'lodash'

import LabeledInput from 'modules/core/components/LabeledInput'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import ScrollableList from 'modules/core/components/ScrollableList'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

type childData = {
  value: number
  label: string
}

type parentData = {
  value: number
  label: string
  children: childData[]
}

type filteredParentData = parentData & {
  filteredChildren: childData[]
  indeterminate: boolean
}

type OptionsProps = {
  level: number
  value: number
  label: string
  isChecked?: boolean
  parentSelected?: boolean
  indeterminate?: boolean
  onChange?: (value: number) => void
}

function TrainingClassOption({
  level,
  value,
  label,
  isChecked = false,
  parentSelected = false,
  indeterminate = false,
  onChange,
}: OptionsProps) {
  const [isHover, setIsHover] = useState<boolean>(false)

  const getBgColor = (): Colors => {
    if (parentSelected) return 'grey300'
    if (isChecked) return isHover ? 'viridian200' : 'viridian100'
    if (isHover) return 'grey100'

    return 'grey000White'
  }

  const color = isChecked ? 'viridian1200' : 'grey800'

  const paddingLeft = level == 1 ? 's32' : 's16'

  const onClick = () => {
    onChange && onChange(value)
  }

  return (
    <Box
      onClick={onClick}
      alignItems="center"
      background={getBgColor()}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      cursor="pointer"
      flexDirection="row"
      gap="s16"
      paddingLeft={paddingLeft}
      paddingRight="s16"
      paddingY="s12"
      width="full"
    >
      <InputCheckBox
        checked={isChecked || parentSelected}
        label={label}
        disabled={parentSelected}
        indeterminate={indeterminate}
      />
      <Text.Normal size="200" weight="medium" color={color}>
        <Box gap="s6" alignItems="center" flexDirection="row">
          {label}
        </Box>
      </Text.Normal>
    </Box>
  )
}

export interface Props {
  isDisabled?: boolean
  placeholder: string
  searchPlaceholder?: string
  data: parentData[]
  onChange: (selectedParents: number[], selectedChilds: number[]) => void
}

export default function TrainingClassFilter({
  isDisabled,
  placeholder,
  searchPlaceholder,
  data,
  onChange,
}: Props) {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const filteredData = useUnsafeMemo<filteredParentData[]>(() => {
    if (!data) return []

    return (
      data
        //convert 'parentData' to 'filteredParentData' which we use internally
        .map((parent): filteredParentData => {
          const children = parent.children.map(
            (child): childData => ({
              label: child.label,
              value: child.value,
            })
          )
          return {
            label: parent.label,
            value: parent.value,
            children: children,
            filteredChildren: children.filter((x) => matchText(debouncedSearch, x.label)),
            indeterminate: false,
          }
        })
        //show only parents which has childs or search matches
        .filter((x) => x.filteredChildren.length || matchText(debouncedSearch, x.label))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, data])

  const [selectedParents, setSelectedParents] = useState<number[]>([])
  const [selectedChilds, setSelectedChilds] = useState<number[]>([])

  // returns the trigger label depending on selection
  const valueLabel = (() => {
    // Define label functions for each case
    const labelStrategies = {
      singleParentSelected: () => {
        const parentLabel = data.find((option) => selectedParents[0] === option.value)?.label
        return `${placeholder}: ${parentLabel}`
      },
      singleChildSelected: () => {
        const parent = data.find((option) =>
          option.children.find((child) => selectedChilds[0] === child.value)
        )
        if (!parent) throw 'How is possible? parent not found for child'

        const child = parent.children.find((child) => selectedChilds[0] === child.value)
        if (!child) throw 'How is possible? child not found'

        return `${placeholder}: ${parent.label}, ${child.label}`
      },
      multipleSelected: () => {
        const totalSelected = selectedParents.length + selectedChilds.length
        return i18n.t('common.filter_selected', { placeholder, totalSelected })
      },
      noneSelected: () => placeholder,
    }

    // Determine which function to call
    const strategyLabel =
      selectedParents.length === 1 && selectedChilds.length === 0
        ? 'singleParentSelected'
        : selectedParents.length === 0 && selectedChilds.length === 1
        ? 'singleChildSelected'
        : selectedParents.length + selectedChilds.length > 0
        ? 'multipleSelected'
        : 'noneSelected'

    // Execute and return the result of the appropriate label function
    return labelStrategies[strategyLabel]()
  })()

  const handleToggle = (isOpen: boolean) => {
    if (!isOpen) {
      onChange(selectedParents, selectedChilds)
    }
  }

  const onChangeParent = (value: number) => {
    // add or remove from selected
    setSelectedParents(xor(selectedParents, [value]))
    // If selected, remove childs (selectedParents is not yet updated)
    if (!selectedParents.includes(value)) {
      const parent = data.find((option) => value == option.value)
      if (!parent) throw 'How is posible? parent not found'
      const childValues = parent.children.map((child) => child.value)
      setSelectedChilds(selectedChilds.filter((x) => !childValues.includes(x)))
    }
  }

  const onChangeChild = (value: number) => {
    const parent = filteredData.find((option) =>
      option.children.find((child) => value === child.value)
    )
    if (!parent) throw 'How is possible? parent not found for child'
    const isParentSelected = selectedParents.includes(parent.value)
    const isCurrentlyChecked = selectedChilds.includes(value)
    const allChildValues = parent.children.map((child) => child.value)

    // Define strategies as an object literal with functions
    const strategies = {
      uncheckParent: () => {
        // uncheck parent
        setSelectedParents(selectedParents.filter((x) => x !== parent.value))

        //select all childs except this
        setSelectedChilds(
          selectedChilds.concat(
            parent.children.map((child) => child.value).filter((childId) => childId !== value)
          )
        )

        parent.indeterminate = true
      },
      selectParent: () => {
        //select parent
        setSelectedParents(selectedParents.concat([parent.value]))

        // deselect this parent childs
        setSelectedChilds(selectedChilds.filter((x) => !allChildValues.includes(x)))
        parent.indeterminate = false
      },
      updateChildSelection: () => {
        const newChildsIds = isCurrentlyChecked
          ? selectedChilds.filter((x) => x !== value)
          : selectedChilds.concat([value])
        if (parent.children.every((child) => newChildsIds.includes(child.value))) {
          //if all childs are selected, select parent and deselect all childs
          strategies.selectParent()
        } else {
          // if not all childs are selected, update selected childs (zero or some childs selected)
          setSelectedChilds(newChildsIds)
          //set intermediate if there is any child selected
          parent.indeterminate = newChildsIds.some((x) => allChildValues.includes(x))
        }
      },
    }

    // Determine which function to call
    const strategyLabel = isParentSelected ? 'uncheckParent' : 'updateChildSelection'

    // Execute the corresponding strategy
    strategies[strategyLabel]()
  }

  return (
    <PopoverMenu type="list" onClick={handleToggle}>
      <PopoverMenu.Trigger disabled={isDisabled}>
        <LabeledInput>
          {
            <Box
              paddingX="s16"
              paddingY="s8"
              flexDirection="row"
              height="s40"
              alignItems="center"
              width="full"
              cursor="pointer"
            >
              <Box flexDirection="row" width="full" gap="s6">
                <CounterBadge count={selectedParents.length + selectedChilds.length} />
                <Box overflowX="hidden" textAlign="left">
                  <Text
                    size="300"
                    color={selectedParents.length + selectedChilds.length ? 'grey1000' : 'grey600'}
                    overflowWrap
                    ellipsis
                  >
                    {valueLabel}
                  </Text>
                </Box>
              </Box>
              <Icon.Small icon="caret-down" />
            </Box>
          }
        </LabeledInput>
      </PopoverMenu.Trigger>

      <PopoverMenu.Content side="bottom" align="center" width="s400">
        <Box paddingY="s16" paddingX="s32">
          <Box
            border={{
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }}
            borderRadius={{
              all: 'abs008',
            }}
            overflowX="hidden"
          >
            <Box width="full" padding="s8" background="grey200">
              <SearchField
                variant="big"
                search={search}
                onSearch={onSearch}
                placeholder={searchPlaceholder || i18n.t('common.search')}
              />
            </Box>
            <ScrollableList>
              {filteredData.map((firstLevel) => (
                <>
                  <TrainingClassOption
                    level={0}
                    value={firstLevel.value}
                    label={firstLevel.label}
                    onChange={onChangeParent}
                    isChecked={selectedParents.includes(firstLevel.value)}
                    indeterminate={firstLevel.indeterminate}
                  />
                  {(search || firstLevel.filteredChildren.length > 1) &&
                    firstLevel.filteredChildren.map((child, index) => (
                      <TrainingClassOption
                        key={index}
                        level={1}
                        value={child.value}
                        label={child.label}
                        onChange={onChangeChild}
                        isChecked={selectedChilds.includes(child.value)}
                        parentSelected={selectedParents.includes(firstLevel.value)}
                      />
                    ))}
                </>
              ))}
            </ScrollableList>
          </Box>
        </Box>
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}
